import React from 'react'
import { Box, Button, Typography } from "@mui/material"
import useSWR from "swr"
import { WagerReward } from "./types"
import { apiFetch, fetcher, isAPIImage } from "../../utils"
import ItemPopover from '../../components/Item/ItemPopover'
import { Item } from '../../shared/item.interface'
import { createSuccessToast } from '../../components/Toasts/SuccessToasts'
import { createErrorToast } from '../../components/Toasts/ErrorToasts'
import RewardRulesModal from './RewardRulesModal'

const WagerRewardRow = ({ reward, claimReward }: { reward: WagerReward, claimReward: () => void }) => {

    const Requirement = ({ image, requirement }: { image: string, requirement: number}) => (
        <Box display="flex" alignItems="center" gap="1rem">
            <Box component="img" src={isAPIImage(image)} sx={{ width: '48px', height: 'auto' }} />     
            <Typography component="span">
                <Typography component="span" sx={{ color: 'var(--text-secondary)' }}>Wager</Typography> <b>${Intl.NumberFormat("en-US").format(requirement)}</b>
            </Typography>

        </Box>
    )

    const Reward = ({ value, item }: { value?: number, item?: Item }) => (
        <Box display="flex" alignItems="center" gap="1rem">
          {reward.value ? (
            <Typography sx={{ textAlign: 'end' }}><b>${Intl.NumberFormat("en-US").format(reward.value)}</b> Reward</Typography>
          ) : reward.item && (
            <ItemPopover item={reward.item} anchor={
              <Box display="flex" alignItems="center" gap=".5rem">
                <Box component="img" src={isAPIImage(reward.item.image)} alt={reward.item.name} sx={{ height: '32px', width: '32px' }} />
                <Typography>{reward.item.name}</Typography>
              </Box>
            } />
          )}
        </Box>
    )

    return (
    <Box 
        display="flex" 
        alignItems="center" 
        gap="1rem" 
        justifyContent="space-between"
        sx={{ p: '1rem', bgcolor: 'var(--primary-bg-soft)', border: '1px solid var(--primary-border)', borderRadius: '8px' }
    }>

 
        <Requirement image={reward.image} requirement={reward.requirement} />
        <Reward value={reward.value} item={reward.item} />
        
        <Button disabled={!reward.isClaimable || reward.isClaimed} variant="contained" sx={{ textTransform: 'capitalize' }} onClick={() => claimReward()}>
            {reward.isClaimed ? "Claimed" : "Claim"}
        </Button>

        </Box>
    )

}


const Page = () => {

    const { data, isLoading, mutate } = useSWR<WagerReward[]>("/reward", fetcher)
    const wagerStats = useSWR<{ wagered: number }>("/reward/stats", fetcher)

    function onRewardClaim(rewardId: string): void {
        mutate((prev) => {
            if(!prev) return prev

            return prev.map((reward) => (reward._id === rewardId ? { ...reward, isClaimed: true } : reward))
        }, {
            revalidate: false
        })
    }


    async function claimReward(rewardId: string): Promise<void> {
        try {
            const data = await apiFetch("/reward/claim", "POST", {
                body: JSON.stringify({
                rewardId: rewardId
                })
            })

            if (!data) return createErrorToast("Something went wrong")

            createSuccessToast(data.message || "Claimed")
            onRewardClaim(rewardId)
            
        } catch (e) {
            createErrorToast((e as Error).message || "Something went wrong")
        }
    }

    return (
        <Box
            display="flex"
            gap="2rem"
            width="100%"
        >
            <Box display="flex" flexDirection="column" className="page" maxWidth="1200px" flexGrow={1} sx={{ px: { xs: '8px', md: '1rem' }}}>
                <Box display="flex" flexDirection="column" alignItems="center" gap=".5rem" sx={{ mb: '3rem '}}>
                    <Typography variant="h3" sx={{ textAlign: 'center' }}>Stake Levels</Typography>
                    <Typography component="span" sx={{ color: 'var(--text-secondary)', mb: '.5rem' }}>Exclusive Rewards for players supporting us on <Typography component="b" sx={{ fontWeight: 'bold', color: 'var(--text-primary)'}}>Stake!</Typography></Typography>
                    <RewardRulesModal />
                    <Typography fontWeight="600" marginTop="1rem" sx={{ color: 'var(--success)' }}>Wagering on house games is allowed.</Typography>
                </Box>

                {data && wagerStats.data && (
                    <Typography sx={{ paddingBottom: '1rem', textAlign: 'center'   
                    }}>{Intl.NumberFormat("en-US").format((data.find((reward) => reward.requirement > wagerStats.data!.wagered)?.requirement || 0) -  wagerStats.data.wagered)} left until you reach the next level.</Typography>

                )}
            
                <Box display="flex" flexDirection="column" gap="1rem">
                    {isLoading ? (
                    <></>
                    ) : !data ? (
                    <Typography>Something went wrong...</Typography>
                    ) : (
                    <>
                        {data.map((reward, index) => (
                        <WagerRewardRow key={reward._id} reward={reward}  
                            claimReward={() => claimReward(reward._id)} 
                        />
                        ))}
                    </>
                    )}
            </Box>
          </Box>
        </Box>
    )
}

export default Page